import { User } from 'domain/user'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputMask } from 'primereact/inputmask'
import { InputText } from 'primereact/inputtext'
import { Stepper } from 'primereact/stepper'
import { StepperPanel } from 'primereact/stepperpanel'
import { ToggleButton } from 'primereact/togglebutton'
import React, { useRef } from 'react'
import { onInputChange } from 'shared/utils/onInputChange'
import { onMaskedInputChange } from 'shared/utils/onMaskedInputChange'
import './user-stepper.scss'

export interface UserDetailsProps {
  showDialog: boolean
  isLoading: boolean
  hideDialog: () => void
  submitted: boolean
  user: User
  setUser: (user: User) => void
  saveUser: () => void
}

const UserStepper: React.FC<UserDetailsProps> = ({
  hideDialog,
  saveUser,
  setUser,
  showDialog,
  isLoading,
  submitted,
  user,
}) => {
  const stepperRef = useRef<any>(null)

  const dialogHeader = (
    <div className='users-dialog-header'>
      <i className='pi pi-user users-dialog-icon' />
      <span>{user.id ? 'Editar Usuário' : 'Novo Usuário'}</span>
    </div>
  )

  const dialogFooter = (
    <div className='users-dialog-footer'>
      <Button
        label='Cancelar'
        icon='pi pi-times'
        outlined
        className='users-btn-cancel'
        onClick={hideDialog}
      />
    </div>
  )

  return (
    <Dialog
      visible={showDialog}
      style={{ width: '820px' }}
      header={dialogHeader}
      footer={dialogFooter}
      modal
      className='users-dialog'
      onHide={hideDialog}
    >
      <Stepper ref={stepperRef}>

        {/* Step 1 — Informações Pessoais */}
        <StepperPanel header='Informações Pessoais'>
          <div className='grid pt-2'>
            <div className='col-6'>
              <label htmlFor='name' className='users-label'>
                <i className='pi pi-user' />
                Nome Completo *
              </label>
              <InputText
                id='name'
                value={user.name}
                onChange={(e) => onInputChange(e, 'name', setUser)}
                required
                autoFocus
                placeholder='Ex: João Silva Santos'
                className={`users-input ${submitted && !user.name ? 'p-invalid' : ''}`}
              />
              {submitted && !user.name && (
                <small className='users-error'>Nome é obrigatório.</small>
              )}
            </div>

            <div className='col-6'>
              <label htmlFor='email' className='users-label'>
                <i className='pi pi-envelope' />
                Email *
              </label>
              <InputText
                id='email'
                value={user.email}
                onChange={(e) => onInputChange(e, 'email', setUser)}
                required
                placeholder='exemplo@empresa.com'
                className={`users-input ${submitted && !user.email ? 'p-invalid' : ''}`}
              />
              {submitted && !user.email && (
                <small className='users-error'>Email é obrigatório.</small>
              )}
            </div>

            <div className='col-6'>
              <label htmlFor='phoneNumber' className='users-label'>
                <i className='pi pi-phone' />
                Número de Telefone
              </label>
              <InputMask
                id='phoneNumber'
                value={user.phoneNumber}
                mask='(99) 99999-9999'
                onChange={(e) => onMaskedInputChange(e, 'phoneNumber', setUser)}
                placeholder='(11) 99999-9999'
                className='users-input'
              />
            </div>
          </div>

          <div className='flex pt-4 justify-content-end'>
            <Button
              label='Próximo'
              icon='pi pi-arrow-right'
              iconPos='right'
              onClick={() => stepperRef.current?.nextCallback()}
            />
          </div>
        </StepperPanel>

        {/* Step 2 — Credenciais de Acesso */}
        <StepperPanel header='Credenciais de Acesso'>
          <div className='grid pt-2'>
            <div className='col-6'>
              <label htmlFor='login' className='users-label'>
                <i className='pi pi-sign-in' />
                Nome de Usuário *
              </label>
              <InputText
                id='login'
                value={user.login}
                onChange={(e) => onInputChange(e, 'login', setUser)}
                required
                placeholder='usuario.sistema'
                className={`users-input ${submitted && !user.login ? 'p-invalid' : ''}`}
              />
              {submitted && !user.login && (
                <small className='users-error'>Login é obrigatório.</small>
              )}
            </div>

            <div className='col-6'>
              <label htmlFor='password' className='users-label'>
                <i className='pi pi-lock' />
                Senha
              </label>
              <InputText
                id='password'
                type='password'
                value={user.password}
                onChange={(e) => onInputChange(e, 'password', setUser)}
                placeholder={user.id ? 'Nova senha' : 'Digite a senha'}
                className={`users-input ${submitted && !user.id && !user.password ? 'p-invalid' : ''}`}
              />
              {user.id && (
                <small style={{ color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                  Deixe em branco para manter a senha atual.
                </small>
              )}
              {submitted && !user.id && !user.password && (
                <small className='users-error'>Senha é obrigatória para novos usuários.</small>
              )}
            </div>
          </div>

          <div className='flex pt-4 justify-content-between'>
            <Button
              label='Voltar'
              severity='secondary'
              icon='pi pi-arrow-left'
              onClick={() => stepperRef.current?.prevCallback()}
            />
            <Button
              label='Próximo'
              icon='pi pi-arrow-right'
              iconPos='right'
              onClick={() => stepperRef.current?.nextCallback()}
            />
          </div>
        </StepperPanel>

        {/* Step 3 — Configurações da Conta */}
        <StepperPanel header='Configurações da Conta'>
          <div className='grid pt-2'>
            <div className='col-6'>
              <label className='users-label'>
                <i className='pi pi-star' />
                Contato Principal
              </label>
              <ToggleButton
                checked={user.mainContactPerson}
                onChange={(e) => setUser({ ...user, mainContactPerson: e.value })}
                onLabel='É Contato Principal'
                offLabel='Não é Contato Principal'
                onIcon='pi pi-star-fill'
                offIcon='pi pi-star'
                style={{ width: '100%', height: '3rem' }}
              />
            </div>

            <div className='col-6'>
              <label className='users-label'>
                <i className='pi pi-ban' />
                Status da Conta
              </label>
              <ToggleButton
                checked={user.blocked}
                onChange={(e) => setUser({ ...user, blocked: e.value })}
                onLabel='Bloqueado'
                offLabel='Ativo'
                onIcon='pi pi-ban'
                offIcon='pi pi-check-circle'
                style={{ width: '100%', height: '3rem' }}
              />
            </div>
          </div>

          <div className='flex pt-4 justify-content-between'>
            <Button
              label='Voltar'
              severity='secondary'
              icon='pi pi-arrow-left'
              onClick={() => stepperRef.current?.prevCallback()}
            />
            <Button
              label='Salvar'
              icon='pi pi-check'
              loading={isLoading}
              className='users-btn-save'
              onClick={saveUser}
            />
          </div>
        </StepperPanel>

      </Stepper>
    </Dialog>
  )
}

export default UserStepper
